import {ClientUser, isSameLogin, Roles, Sex} from "./user";

export interface LocalRoom {
    title: string,
}

export interface ServerRoom extends LocalRoom {
    createdBy: string,
    password: boolean,
    allowedGenders: Array<Sex>,
    allowOnlyRegistered: boolean,
    allowOnlyPremium: boolean,
    pinnedMessage: string,
    type: RoomType,
    moderators: Array<ModeratorInfo>,
    isPaid?: boolean
}

export enum RoomType {
    /**
     * Основная комната, гостиная
     */
    MAIN_ROOM = 'main',
    /**
     * Комната для глобальных админов чата
     */
    MODERATORS_ROOM = 'moderators',
    /**
     * Сервисная комната для отображения блокировок
     */
    SERVICE_ROOM = 'service',
    /**
     * Комната созданная пользователем
     */
    USER_ROOM = 'user',
    /**
     * Платная комната созданная пользователем
     */
    PAID_ROOM = 'paid',
    /**
     * Комната между двумя пользователями
     */
    PRIVATE_ROOM = 'private',
}

export interface ServerRoomWithMembers extends ServerRoom {
    members: Array<ClientUser>
}

export class ServerRoomWithMembersClass implements ServerRoomWithMembers {
    constructor(
        public title: string,
        public type: RoomType,
        public createdBy: string,
        public members: Array<ClientUser>,
        public password: boolean,
        public allowedGenders: Sex[],
        public allowOnlyRegistered: boolean,
        public allowOnlyPremium: boolean,
        public pinnedMessage: string,
        public moderators: Array<ModeratorInfo>,
        public isPaid?: boolean
    ) {
    }
}

/**
 * Список ролей для локальных модераторов,
 */
export enum LocalModeratorPermissions {
    BASE_PERMISSION = 'base_permission', //Данное разрешение есть по умолчанию у всех модераторов, дает право на блокировку и удаление сообщений
    ADD_REMOVE_MODERATORS = 'add_remove_moderators',
    EDIT_PINNED_MESSAGE = 'edit_pinned_message',
    EDIT_BAN_REASONS = 'edit_ban_reasons',
    UNBLOCK_USERS = 'unblock_users',
    EDIT_ROOM_PASSWORD = 'edit_room_password',
}

/**
 * Ограничения на пароль комнаты. Одинаковые для клиента и сервера.
 */
export const ROOM_PASSWORD_MIN_LENGTH = 3
export const ROOM_PASSWORD_MAX_LENGTH = 16

/**
 * Пустая строка означает снятие пароля.
 */
export function isRoomPasswordValid(password: string): boolean {
    const trimmed = (password ?? '').trim()

    if (!trimmed) return true

    return trimmed.length >= ROOM_PASSWORD_MIN_LENGTH && trimmed.length <= ROOM_PASSWORD_MAX_LENGTH
}

export interface ModeratorInfo {
    login: string,
    permissions: Array<LocalModeratorPermissions>,
}

export function createModeratorInfo(login: string, permissions: Array<LocalModeratorPermissions>): ModeratorInfo {
    return {login, permissions}
}

export class RoomUtils {

    static isUserHasPermission(room: ServerRoom, login: string, permission: LocalModeratorPermissions): boolean {
        if (!room) return false

        if (room.type !== RoomType.PAID_ROOM) return false

        //Создателю разрешено все
        if (isSameLogin(room.createdBy, login)) return true

        const info = RoomUtils.moderatorInfo(room, login)

        if (!info) return false

        if (info && permission === LocalModeratorPermissions.BASE_PERMISSION) {
            return true
        }

        return info.permissions.includes(permission)
    }

    static hasNonBasePermission(room: ServerRoom, login: string,): boolean {
        return RoomUtils.moderatorInfo(room, login)?.permissions.filter(p => p !== LocalModeratorPermissions.BASE_PERMISSION).length > 0
    }

    static moderatorInfo(room: ServerRoom, login: string): ModeratorInfo | undefined {
        if (!room) return undefined

        if (room.type !== RoomType.PAID_ROOM) return undefined;

        if (isSameLogin(room.createdBy, login)) {
            //У создателя комнаты все права
            return {
                login: login,
                permissions: [
                    LocalModeratorPermissions.BASE_PERMISSION,
                    LocalModeratorPermissions.ADD_REMOVE_MODERATORS,
                    LocalModeratorPermissions.EDIT_PINNED_MESSAGE,
                    LocalModeratorPermissions.EDIT_BAN_REASONS,
                    LocalModeratorPermissions.EDIT_ROOM_PASSWORD,
                ]
            }
        }

        return room.moderators.find((m) => isSameLogin(m.login, login));
    }
}

